import { ReactNode, useEffect, useState } from 'react';
import { matchPath } from 'react-router-dom';

import {
    TertiaryLayoutSkeleton,
    useCheckIsLoggedIn,
    usePermissionsCheck,
} from '@speeki/client-admin';
import { useFeatureFlagsStatus } from '@speeki/feature-flags';
import { useEffectOnMountUnsafe } from '@speeki/react-hooks';

import { getDecodedToken } from '@utils/helpers';

import { authService } from '@services/AuthService/AuthService';
import { sentry } from '@services/SentryService/SentryService';

interface AppLoadingProps {
    children: ReactNode;
}

/**
 * Add to this component only async stuff that is required for the proper working of the app.
 */
export const AppLoading = ({ children }: AppLoadingProps) => {
    const { permissionsCheck } = usePermissionsCheck();
    const { flagsReady } = useFeatureFlagsStatus();

    const [initialAppLoading, setInitialAppLoading] = useState(true);

    const { checkIsLoggedIn, checkIsLoggedInLoading } =
        // @ts-expect-error FIXME: instance class type
        useCheckIsLoggedIn(authService, {
            onAfterUserQuery: () => {
                void sentry.setUser();
            },
        });

    const match = (path: string) => !!matchPath(path, window.location.pathname);

    const isRoot = match('/');
    const isHomepage = match('/dashboard');
    const isProgrammes = match('/programmes') || match('/programmes/*');
    const isIncidentsDashboard =
        match('/incidents/dashboard') || match('/incidents/dashboard/');
    const isCarbonLens = match('/carbon-lens');

    const isDemoUser = permissionsCheck(['DEMO_FEATURES']);

    const hasFrameworksAccess = permissionsCheck(['FRAMEWORKS_DASHBOARD']);
    const hasProgrammesAccess = permissionsCheck(
        ['ESG_DASHBOARD', 'ESG_QUESTIONNAIRE'],
        true,
    );
    const hasIncidentsAccess = permissionsCheck(['DASHBOARD']);

    const loading = !flagsReady || checkIsLoggedInLoading;

    const redirectFromHome =
        !loading &&
        !isDemoUser &&
        (isHomepage || isRoot) &&
        (hasFrameworksAccess || hasProgrammesAccess || hasIncidentsAccess);

    const redirectToDashboard =
        !loading &&
        !isDemoUser &&
        !isRoot &&
        !isHomepage &&
        !isProgrammes &&
        !isIncidentsDashboard &&
        !isCarbonLens;

    useEffectOnMountUnsafe(() => {
        void checkIsLoggedIn();
    });

    useEffect(() => {
        if (loading || !initialAppLoading) return;

        const decodedToken = getDecodedToken();
        const isExternal = decodedToken?.user.type === 'EXTERNAL';

        if (!decodedToken) return;

        if (isExternal) {
            window.location.href = window.location.origin + '/tasks';
            return;
        }

        if (redirectFromHome) {
            // Priority: frameworks → programmes → incidents dashboard (mirrors auth post-login redirect)
            const dest = hasFrameworksAccess
                ? '/frameworks'
                : hasProgrammesAccess
                  ? '/programmes'
                  : '/incidents/dashboard';
            window.location.href = window.location.origin + dest;
            return;
        }

        if (redirectToDashboard)
            window.location.href = window.location.origin + '/dashboard';

        setInitialAppLoading(false);
    }, [
        hasFrameworksAccess,
        hasIncidentsAccess,
        hasProgrammesAccess,
        redirectFromHome,
        redirectToDashboard,
        isProgrammes,
        isIncidentsDashboard,
        isRoot,
        isHomepage,
        loading,
    ]);

    if (redirectToDashboard) return <TertiaryLayoutSkeleton />;

    return (
        <>
            {loading || initialAppLoading ? (
                <TertiaryLayoutSkeleton />
            ) : (
                children
            )}
        </>
    );
};
